/**
 * The single-session guard's trigger half, and the grace deadline. Mirrored
 * from `bimgo-ai-web/client/src/hooks/useSessionGuard.ts` (their PR #167),
 * adapted to 2.5's lazy `supabase()` client. #75.
 *
 * Supabase enforces "single session per user" server-side but tells nobody: the
 * losing device's token stays syntactically valid, so it acts signed in until it
 * next calls an API or refreshes — up to an hour of a dead session. This hook is
 * the missing doorbell. Every signed-in device joins one Realtime channel per
 * user and announces itself on connect; `decideOnAnnouncement` turns that stream
 * into a decision (ignore our own session, ignore an older one, otherwise start
 * a grace anchored to the winner's login), and nothing is probed until the
 * deadline — because probing destroys the session being deliberately kept alive
 * (see `checkForRevokedSession`).
 *
 * The announcement is still never trusted. It decides only when to *ask*
 * GoTrue; the sign-out itself always waits for GoTrue's answer. A forged message
 * costs a cosmetic banner that clears itself at the deadline — it cannot sign
 * anybody out. That is what lets the channel stay public and this stay
 * frontend-only.
 *
 * **What is kept from 2.5, deliberately.** On focus the probe only runs when the
 * token is expired or about to be (owner, 2026-08-25): `refreshSession()` mints
 * a new token every time, and a valid token proves nothing new by being
 * re-issued. The web client probes on every focus. Mid-grace the question is
 * different anyway — the deadline, not the token — so the owner's rule applies
 * only to the no-grace branch.
 */
import { useEffect, useMemo, useState } from "react";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { supabase } from "../lib/supabaseClient";
import { tokenNeedsRefresh } from "../lib/accessToken";
import {
  checkForRevokedSession,
  clearGrace,
  decideOnAnnouncement,
  dropForeignGrace,
  getGrace,
  readSessionClaims,
  startGrace,
  subscribeGrace,
  type SessionGrace,
} from "../lib/authSession";
import { authLog } from "../lib/authDebug";

const SIGNED_IN_EVENT = "signed-in";

/** What one device tells the others when it connects. */
interface SignedInAnnouncement {
  /** The sender's `auth.sessions` row id, so a device can spot itself. */
  sessionId?: unknown;
  /** Epoch seconds of the sender's login, so age can be compared. */
  loginAt?: unknown;
}

/**
 * Serialises teardown → setup for the one topic this hook uses. realtime-js
 * hands back the *existing* channel for a topic and `subscribe()` is a no-op
 * unless that channel is closed; removing a channel is async, so signing out and
 * straight back in as the same user would otherwise get the still-leaving channel
 * and silently skip subscribe, leaving the guard dead until a reload.
 */
let channelLifecycle: Promise<unknown> = Promise.resolve();

function chainChannelWork(step: () => unknown): void {
  channelLifecycle = channelLifecycle.then(step).catch((error) => {
    authLog("channel work failed", error);
  });
}

/** Subscribes a component to the pending sign-out deadline, for the banner. */
export function useSessionGrace(): SessionGrace | null {
  const [grace, setGrace] = useState<SessionGrace | null>(() => getGrace());
  useEffect(() => subscribeGrace(setGrace), []);
  return grace;
}

export function useSessionGuard(
  session: { access_token?: string; user?: { id?: string } } | null,
): void {
  const userId = session?.user?.id ?? null;
  const accessToken = session?.access_token;

  // `sessionId` and `loginAt` are stable for the life of a session, which is
  // what lets the effect below depend on them rather than on the token and
  // avoid rejoining the channel every hour. (`AuthProvider` retains the session
  // object across rotations anyway, so this token may be the session's first —
  // the claims are the same ones.)
  const { sessionId, loginAt } = useMemo(() => readSessionClaims(accessToken), [accessToken]);

  useEffect(() => {
    if (!userId) return;

    let disposed = false;
    let channel: RealtimeChannel | null = null;
    let deadlineTimer: ReturnType<typeof setTimeout> | null = null;
    const client = supabase();

    // A deadline stored by a session that is no longer ours would otherwise
    // suppress this one's checks. Signing out and back in is exactly when that
    // happens.
    if (sessionId) dropForeignGrace(sessionId);

    const probe = (why: string) => {
      authLog("probing:", why);
      void checkForRevokedSession().catch(() => {});
    };

    // The owner's rule: on focus, only when the token is due. See the header.
    const probeIfDue = (why: string) => {
      void client.auth
        .getSession()
        .then(({ data }) => {
          if (tokenNeedsRefresh(data.session?.expires_at)) probe(why);
          else authLog("skipped:", why, "— token still valid");
        })
        .catch(() => probe(why));
    };

    /** Runs the confirmation once the deadline has passed; re-arms if not. */
    const enforce = () => {
      if (disposed) return;
      const grace = getGrace();
      if (!grace) return;
      if (Date.now() < grace.deadlineAt) {
        armDeadline();
        return;
      }

      authLog("grace elapsed, confirming with GoTrue");
      void checkForRevokedSession({ force: true })
        .then((result) => {
          if (disposed) return;
          if (result === "valid") {
            // A false alarm: the winning device signed out again before the
            // deadline, or the announcement was forged. Nothing was lost — take
            // the banner down and carry on.
            authLog("grace elapsed but this session is still current");
            clearGrace();
            return;
          }
          // "revoked" — supabase-js has already dropped the session and
          // `probeSession` cleared the deadline; App renders the login page.
          // "unknown" — a network failure, so leave the deadline due and let the
          // next focus retry rather than guess.
          if (result === "unknown") authLog("confirmation inconclusive, will retry");
        })
        .catch(() => {});
    };

    const armDeadline = () => {
      if (deadlineTimer) {
        clearTimeout(deadlineTimer);
        deadlineTimer = null;
      }
      const grace = getGrace();
      if (!grace) return;
      const wait = grace.deadlineAt - Date.now();
      if (wait <= 0) {
        enforce();
        return;
      }
      // Best effort only. Timers are throttled or frozen in a backgrounded
      // window, so the focus and visibility listeners below are what actually
      // guarantee the deadline is honoured.
      deadlineTimer = setTimeout(enforce, wait);
    };

    // Re-verify whenever this window comes back to the user. Installed always,
    // not just on socket failure: a backgrounded window can sit with a dead
    // socket (phoenix refuses to reconnect while hidden) and miss every
    // announcement; broadcast is fire-and-forget with no replay; and a proxy
    // refusing the WebSocket upgrade would otherwise make this a silent no-op.
    // Both listeners because WebView2 alt-tabbing back from Revit fires window
    // 'focus' but not document visibility.
    const onWake = (why: string) => {
      // Mid-grace this is the deadline check, not a probe: the answer is
      // already known and asking again would end the session early.
      if (getGrace()) {
        enforce();
        return;
      }
      probeIfDue(why);
    };
    const onFocus = () => onWake("window regained focus");
    const onVisibilityChange = () => {
      if (!document.hidden) onWake("tab became visible");
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);
    const unwatchFocus = () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };

    // Resume a deadline that survived a reload.
    armDeadline();

    const topic = `session-guard:${userId}`;

    chainChannelWork(async () => {
      if (disposed) return;

      // Backstop for the channel-reuse hazard: remove any stale channel left for
      // this topic before subscribing, or the subscription is silently swallowed
      // and the device never announces itself again.
      const stale = client
        .getChannels()
        .filter((existing) => existing.topic === `realtime:${topic}`);
      if (stale.length > 0) {
        authLog("removing stale channel(s):", stale.map((c) => c.state));
        await Promise.all(stale.map((c) => client.removeChannel(c)));
        if (disposed) return;
      }

      authLog("joining channel for user", userId);
      const joined = client.channel(topic, { config: { broadcast: { self: false } } });
      channel = joined;

      joined.on("broadcast", { event: SIGNED_IN_EVENT }, (message) => {
        if (disposed) return;
        const payload = (message?.payload ?? {}) as SignedInAnnouncement;
        const decision = decideOnAnnouncement(
          { sessionId, loginAt },
          {
            sessionId: typeof payload.sessionId === "string" ? payload.sessionId : null,
            loginAt: typeof payload.loginAt === "number" ? payload.loginAt : null,
          },
        );

        switch (decision.kind) {
          case "ignore":
            authLog("ignoring announcement:", decision.reason);
            return;
          case "probe":
            // Not a `probeIfDue`: a peer just announced, which is the one event
            // that makes an otherwise-valid token worth presenting.
            probe("announcement without a comparable login time");
            return;
          case "grace":
            authLog("another device signed in; grace until", new Date(decision.deadlineAt).toISOString());
            // `sessionId` is non-null whenever the decision is "grace".
            startGrace(sessionId as string, decision.deadlineAt);
            armDeadline();
            return;
        }
      });

      joined.subscribe((status) => {
        if (disposed) return;
        authLog("subscribe status:", status);

        if (status === "SUBSCRIBED") {
          // Verify on every connect, not just the first: realtime-js reconnects
          // by itself, so this covers the device that was asleep or offline while
          // the other one signed in. Suppressed automatically while a grace runs.
          // A plain probe rather than `probeIfDue`: a reconnect is exactly the
          // moment a missed announcement would have arrived.
          if (getGrace()) enforce();
          else probe("connected");

          // Announce on every connect too, rather than only on a fresh sign-in.
          // Over-announcing is free only because receivers compare login times
          // before acting — which is why the payload is no longer empty.
          void joined
            .send({
              type: "broadcast",
              event: SIGNED_IN_EVENT,
              payload: { sessionId, loginAt } satisfies SignedInAnnouncement,
            })
            .then((result) => authLog("announced sign-in:", result))
            .catch((error) => authLog("announce failed", error));
          return;
        }

        authLog("realtime unavailable; relying on the focus check");
      });
    });

    return () => {
      disposed = true;
      unwatchFocus();
      if (deadlineTimer) clearTimeout(deadlineTimer);
      authLog("tearing down");
      chainChannelWork(async () => {
        const dying = channel;
        channel = null;
        if (dying) await client.removeChannel(dying);
      });
    };
  }, [userId, sessionId, loginAt]);
}
